/**
 * SessionsDialog
 * Lists the user's active server-side sessions and lets them sign out
 * every session except the one they're currently using.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Monitor, Smartphone, Shield, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString('en-GB');
  } catch {
    return '';
  }
};

const describeUA = (ua) => {
  if (!ua) return 'Unknown device';
  const lc = ua.toLowerCase();
  let device = 'Desktop';
  if (lc.includes('mobile') || lc.includes('iphone') || lc.includes('android')) device = 'Mobile';
  else if (lc.includes('ipad') || lc.includes('tablet')) device = 'Tablet';
  let browser = 'Browser';
  if (lc.includes('edg/')) browser = 'Edge';
  else if (lc.includes('chrome')) browser = 'Chrome';
  else if (lc.includes('firefox')) browser = 'Firefox';
  else if (lc.includes('safari')) browser = 'Safari';
  else if (lc.includes('curl')) browser = 'curl';
  let os = '';
  if (lc.includes('windows')) os = 'Windows';
  else if (lc.includes('mac os') || lc.includes('macos')) os = 'macOS';
  else if (lc.includes('linux')) os = 'Linux';
  else if (lc.includes('android')) os = 'Android';
  else if (lc.includes('iphone') || lc.includes('ipad')) os = 'iOS';
  return [browser, os, device].filter(Boolean).join(' · ');
};

const SessionsDialog = ({ open, onOpenChange }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/auth/sessions`);
      setSessions(res.data.sessions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleRevokeOthers = async () => {
    setRevoking(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/sessions/revoke-others`);
      toast.success(res.data.message || 'Other sessions revoked');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg" data-testid="sessions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Shield className="h-4 w-4 text-amber-400" />
            Active Sessions
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Devices currently signed in to your account. Sign out any you don't recognise.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {sessions.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-8">No active sessions.</p>
            )}
            {sessions.map((s) => {
              const Icon = (s.user_agent || '').toLowerCase().match(/mobile|iphone|android/) ? Smartphone : Monitor;
              return (
                <div
                  key={s.jti}
                  className={`rounded-lg border p-3 flex items-start gap-3 ${
                    s.is_current
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-slate-900/50 border-slate-700/60'
                  }`}
                  data-testid={`session-row-${s.jti}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    s.is_current ? 'bg-emerald-500/20' : 'bg-slate-800'
                  }`}>
                    <Icon className={`h-4 w-4 ${s.is_current ? 'text-emerald-400' : 'text-slate-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white truncate">{describeUA(s.user_agent)}</p>
                      {s.is_current && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-medium">
                          This device
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                      {s.ip || 'Unknown IP'} · Signed in {formatRelative(s.created_at)}
                      {s.last_seen_at && s.last_seen_at !== s.created_at && ` · Last seen ${formatRelative(s.last_seen_at)}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-700">
          <p className="text-[11px] text-slate-500">
            {otherCount === 0
              ? 'Only this device is signed in.'
              : `${otherCount} other device${otherCount > 1 ? 's' : ''} signed in.`}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRevokeOthers}
            disabled={revoking || otherCount === 0}
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8 text-xs"
            data-testid="revoke-other-sessions-btn"
          >
            {revoking ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <LogOut className="h-3 w-3 mr-1.5" />}
            Sign out all other devices
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionsDialog;
